import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { inject } from '@angular/core';
import { IdentityServiceImpl } from './identity.service';

/**
 * Guard para autorizar acceso a las rutas de la aplicación
 * @param route ActivatedRouteSnapshot
 * @param state RouterStateSnapshot
 * @returns true si el usuario está autorizado, false en caso contrario
 */
export const AuthorizationGuard = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const seusService = inject(IdentityServiceImpl);
  const router = inject(Router);

  const doActivate = (state: RouterStateSnapshot): Promise<boolean> => {
    const _url = state.url.split('?')[0];
    return new Promise((resolver) => {
      try {
        if (seusService.isLoggedIn()) {
          checkAuthorization(_url.substring(1), resolver);
        } else {
          requestSsoLoginOrDeny(resolver, () =>
            checkAuthorization(_url.substring(1), resolver)
          );
        }
      } catch (error) {
        console.error(error);
        router.navigate(['access-denied']).catch((err) => console.error(err));
        resolver(false);
      }
    });
  };

  const checkAuthorization = (
    url: string,
    resolver: (param: boolean) => void
  ): void => {
    seusService.isAuthorized(url).subscribe({
      next: (res: boolean) => {
        if (res) {
          resolver(true);
          return;
        }

        requestSsoLoginOrDeny(resolver);
      },
      error: (err) => {
        console.error(err);
        requestSsoLoginOrDeny(resolver);
      }
    });
  };

  const requestSsoLoginOrDeny = (
    resolver: (param: boolean) => void,
    onLoggedInYet?: () => void
  ): void => {
    seusService.getLoginForm().subscribe({
      next: (form: string) => {
        if (form === 'LoggedInYet') {
          if (onLoggedInYet) {
            onLoggedInYet();
            return;
          }
          router.navigate(['access-denied']).catch((err) => console.error(err));
          resolver(false);
          return;
        }
        submitSsoLoginForm(form, resolver);
        resolver(false);
      },
      error: (error) => {
        console.error(error);
        router.navigate(['access-denied']).catch((err) => console.error(err));
        resolver(false);
      }
    });
  };

  const submitSsoLoginForm = (
    formHtml: string,
    resolver: (param: boolean) => void
  ): void => {
    const container = document.createElement('div');
    container.innerHTML = formHtml;

    const loginForm = container.querySelector('form');
    if (loginForm instanceof HTMLFormElement) {
      container.style.display = 'none';
      document.body.appendChild(container);
      loginForm.submit();
      return;
    }

    router.navigate(['access-denied']).catch((err) => console.error(err));
    resolver(false);
  };

  return doActivate(state);
};
